import { decamelize } from 'humps';
import {
  APIGetDiscounts,
  APIGetIncreaseBuyerSublimitRequests,
  APIGetInvoiceAggregates,
  APIGetMarcoWrapped,
  AvailableCreditLine,
  BankAccount,
  BuyerDetails,
  CreditFacility,
  CreditLineMetrics,
  WithdrawMoney,
  ReportParams,
  APICompanyContacts,
  APIGetPaymentDetails,
  APIWithdrawalRequest,
  APIGetFinanceRequestDetail,
  APIGetFinanceRequestStatistics,
  APIGetFinanceRequestInvoices,
  APIFinanceRequestAggregations,
  APIGetInvoiceFields,
  APIFinanceRequestInvoice,
  APIFinanceRequestReviewStatistics,
  APIGetCompletedFinanceRequestDetail,
  APIGetCompletedFinanceRequestInvoices,
  SortingOrder,
  BuyerInformation,
  APIPayment,
  Invoice,
  APITransfer,
  APIBatchData,
  APITransferDetail,
  RevolvingCreditLineClientDetail,
  RevolvingCreditLineClient,
  RevolvingCreditLinePendingCharges,
} from '../types/servicing';
import axios from './axios';
import { PaginatedResponse } from './types';

// AR Aging Data Types
export type AgingDataParams = {
  date_type: string;
  cutoff_date: string;
};

export interface AgingDataResponse {
  client_uuid: string;
  client_name: string;
  buyer_name: string;
  buyer_id: string;
  client_ref: string;
  invoice_number: string;
  po_number: string;
  invoice_date: string;
  batch_number: number;
  invoice_amount: number;
  pmt_date: string | null;
  balance: number;
  age: number;
  current: number | null;
  '1_30'?: number | null;
  '31_60': number | null;
  '61_90': number | null;
  '91_120': number | null;
  '121_plus': number | null;
}

export function getCreditLineMainMetrics(companyId: number): Promise<CreditLineMetrics> {
  return axios
    .get(`/companies/${companyId}/products/factoring/factoring_data/credit_summary/`)
    .then(({ data }) => {
      const simplifiedMetrics: CreditLineMetrics = {
        availableCredit: null,
        creditLineSize: null,
        netFundsEmployed: null,
        cashReserves: null,
        reserveEscrow: null,
      };

      Object.keys(data).forEach((key) => {
        const metric = data[key];
        if (metric && typeof metric.amount === 'number') {
          simplifiedMetrics[key as keyof CreditLineMetrics] = metric.amount;
        }
      });

      return simplifiedMetrics;
    });
}

export function getCreditFacilities(companyId: number): Promise<CreditFacility[]> {
  return axios
    .get(`/companies/${companyId}/products/factoring/factoring_data/credit_facilities/`)
    .then(({ data }) => data);
}

export function getBuyersCompanyInformation(
  companyId: number,
  page: number,
  pageSize: number,
  filters?: any,
  sortBy?: string,
  sortOrder: string = SortingOrder.DESC
): Promise<PaginatedResponse<BuyerInformation>> {
  const params = new URLSearchParams();
  params.append('page', `${page}`);
  params.append('page_size', `${pageSize}`);
  params.append('sort_order', sortOrder);
  if (sortBy) {
    params.append('sort_by', decamelize(sortBy));
  }
  filters &&
    Object.keys(filters).forEach((key) => {
      params.append(key, filters[key]);
    });
  return axios
    .get(`/companies/${companyId}/products/factoring/factoring_data/buyers_balances?${params.toString()}`)
    .then(({ data }) => data);
}

export function getBuyerDetails(companyId: number, buyerId: string): Promise<BuyerDetails> {
  return axios
    .get(`/companies/${companyId}/products/factoring/factoring_data/buyer_information/${buyerId}/`)
    .then(({ data }) => data);
}

export function getAvailableCreditLine(companyId: number): Promise<AvailableCreditLine> {
  return axios
    .get(`/companies/${companyId}/products/factoring/factoring_data/factoring_credit_facility_balances/`)
    .then(({ data }) => data);
}

export function getCompanyUserBanks(companyId: number): Promise<BankAccount[]> {
  return axios
    .get(`/companies/${companyId}/products/factoring/factoring_data/bank_information/`)
    .then(({ data }) => data);
}

export function submitWithdrawalRequest(
  companyId: number,
  payload: WithdrawMoney
): Promise<APIWithdrawalRequest> {
  return axios
    .post(`/companies/${companyId}/products/factoring/services/request_withdrawal/`, payload)
    .then(({ data }) => data);
}

export function getFactoredInvoices(
  companyId: number,
  page = 0,
  pageSize = 10,
  filters?: any,
  isAdmin?: boolean,
  sortBy?: string,
  sortOrder: string = SortingOrder.DESC,
  exactInvoice: boolean = false // determines if the invoice number should be an exact match
): Promise<PaginatedResponse<Invoice>> {
  const params = new URLSearchParams();
  params.append('page', `${page}`);
  params.append('page_size', `${pageSize}`);
  params.append('sort_order', sortOrder);
  params.append('exact_invoice', exactInvoice.toString());
  if (sortBy) {
    params.append('sort_by', decamelize(sortBy));
  }
  filters &&
    Object.keys(filters).forEach((key) => {
      if (filters[key] === 'openAndOverdue') {
        params.append(key, 'open');
        params.append(key, 'overdue');
      } else {
        params.append(key, filters[key]);
      }
    });
  return axios
    .get(
      `${
        isAdmin ? '/admins' : ''
      }/companies/${companyId}/products/factoring/factoring_data/financing_invoices/?${params.toString()}`
    )
    .then(({ data }) => data);
}

export function getInvoicesAggregates(companyId: number, filters?: any): Promise<APIGetInvoiceAggregates> {
  const params = new URLSearchParams();
  filters &&
    Object.keys(filters).forEach((key) => {
      if (filters[key] === 'openAndOverdue') {
        params.append(key, 'open');
        params.append(key, 'overdue');
      } else {
        params.append(key, filters[key]);
      }
    });

  return axios
    .get(
      `/companies/${companyId}/products/factoring/factoring_data/invoice_fin_aggregates/?${params.toString()}`
    )
    .then(({ data }) => data);
}

export function requestReactivationCreditLines(companyId: number): Promise<any> {
  return axios
    .post(`/companies/${companyId}/products/factoring/services/request_credit_line_reactivation/`)
    .then(({ data }) => data);
}

export function getRequestReactivationCreditLines(companyId: number): Promise<any> {
  return axios
    .get(`/companies/${companyId}/products/factoring/services/request_credit_line_reactivation/`)
    .then(({ data }) => data);
}

export function exportInvoices(companyId: number, filters?: any): Promise<Blob> {
  const params = new URLSearchParams();
  filters &&
    Object.keys(filters).forEach((key) => {
      if (filters[key] === 'openAndOverdue') {
        params.append(key, 'open');
        params.append(key, 'overdue');
      } else {
        params.append(key, filters[key]);
      }
    });
  return axios
    .get(
      `/companies/${companyId}/products/factoring/factoring_data/financing_invoices/export/?${params.toString()}`,
      {
        responseType: 'blob',
      }
    )
    .then((response) => response.data);
}

export function submitAddNewBuyerRequest(companyId: number, payload: any) {
  return axios
    .post(`/companies/${companyId}/products/factoring/services/request_add_buyer/`, payload)
    .then(({ data }) => data);
}

export function submitIncreaseBuyerSublimitRequest(companyId: number, payload: any) {
  return axios
    .post(`/companies/${companyId}/products/factoring/services/request_buyer_sublimit_increase/`, payload)
    .then(({ data }) => data);
}

export function getIncreaseBuyerSublimitRequests(
  companyId: number
): Promise<APIGetIncreaseBuyerSublimitRequests> {
  return axios
    .get(`/companies/${companyId}/products/factoring/services/request_buyer_sublimit_increase/`)
    .then(({ data }) => data);
}

export function submitIncreaseCreditLineRequest(companyId: number, payload: any) {
  return axios
    .post(`/companies/${companyId}/products/factoring/services/request_credit_line_increase/`, payload)
    .then(({ data }) => data);
}

export function getPayments(
  companyId: number,
  page = 0,
  pageSize = 0,
  filters?: any,
  sortBy?: string,
  sortOrder: string = SortingOrder.DESC
): Promise<PaginatedResponse<APIPayment>> {
  const params = new URLSearchParams();
  params.append('page', `${page}`);
  params.append('page_size', `${pageSize}`);
  params.append('sort_order', sortOrder);
  if (sortBy) {
    params.append('sort_by', decamelize(sortBy));
  }
  filters && Object.keys(filters).forEach((key) => params.append(key, filters[key]));
  return axios
    .get(`/companies/${companyId}/products/factoring/factoring_data/payments/?${params.toString()}`)
    .then(({ data }) => data);
}

export function getPaymentDetails(
  companyId: number,
  limit = 10,
  skip = 0,
  filters?: any
): Promise<APIGetPaymentDetails> {
  const params = new URLSearchParams();
  params.append('skip', `${skip}`);
  params.append('limit', `${limit}`);
  filters && Object.keys(filters).forEach((key) => params.append(key, filters[key]));
  return axios
    .get(`/companies/${companyId}/products/factoring/factoring_data/payments/details/?${params.toString()}`)
    .then(({ data }) => data);
}

export function getDiscounts(companyId: number, invoiceId: number): Promise<APIGetDiscounts> {
  return axios
    .get(
      `/companies/${companyId}/products/factoring/factoring_data/invoice_financing/${invoiceId}/discounts/`
    )
    .then(({ data }) => data);
}

export function generateReport(
  companyId: number,
  reportKey: string,
  reportParams: ReportParams
): Promise<Blob> {
  const params = new URLSearchParams();
  Object.keys(reportParams).forEach((key) => {
    params.append(decamelize(key), reportParams[key]);
  });
  return axios
    .get(
      `/companies/${companyId}/products/factoring/factoring_data/${reportKey}/export/?${params.toString()}`,
      {
        responseType: 'blob',
      }
    )
    .then(({ data }) => data);
}

export function getMarcoWrapped(companyId: number): Promise<APIGetMarcoWrapped> {
  return axios
    .get(`/companies/${companyId}/products/factoring/factoring_data/wrapped/`)
    .then(({ data }) => data);
}

export function exportBuyers(companyId: number, filters?: any): Promise<Blob> {
  const params = new URLSearchParams();
  filters &&
    Object.keys(filters).forEach((key) => {
      params.append(key, filters[key]);
    });
  return axios
    .get(
      `/companies/${companyId}/products/factoring/factoring_data/buyers_balances/export/?${params.toString()}`,
      {
        responseType: 'blob',
      }
    )
    .then((response) => response.data);
}

export function getCompanyContacts(companyId: number): Promise<APICompanyContacts> {
  return axios
    .get(`/companies/${companyId}/products/factoring/factoring_data/contacts/`)
    .then(({ data }) => data);
}

export function submitFundingRequest(companyId: number, payload: FormData): Promise<any> {
  return axios
    .post(`/companies/${companyId}/products/factoring/services/request_invoice_funding/`, payload)
    .then(({ data }) => data);
}

export function getLastWithdrawalRelease(companyId: number): Promise<APIWithdrawalRequest> {
  return axios
    .get(`/companies/${companyId}/products/factoring/services/request_withdrawal/`)
    .then(({ data }) => data);
}

export function getTransfers(
  companyId: number,
  page = 0,
  pageSize = 10,
  filters?: any,
  sortBy?: string,
  sortOrder: string = SortingOrder.DESC
): Promise<PaginatedResponse<APITransfer>> {
  const params = new URLSearchParams();
  params.append('page', `${page}`);
  params.append('page_size', `${pageSize}`);
  params.append('sort_order', `${sortOrder}`);
  if (sortBy) {
    params.append('sort_by', decamelize(sortBy));
  }
  filters &&
    Object.keys(filters).forEach((key) => {
      params.append(key, filters[key]);
    });
  return axios
    .get(`/companies/${companyId}/products/factoring/factoring_data/transfers/?${params.toString()}`)
    .then(({ data }) => data);
}

export function getTransferDetails(
  companyId: number,
  page = 0,
  page_size = 10,
  filters?: any
): Promise<PaginatedResponse<APITransferDetail>> {
  const params = new URLSearchParams();
  params.append('page', `${page}`);
  params.append('page_size', `${page_size}`);
  filters && Object.keys(filters).forEach((key) => params.append(key, filters[key]));
  return axios
    .get(`/companies/${companyId}/products/factoring/factoring_data/transfers/details/?${params.toString()}`)
    .then(({ data }) => data);
}

export function getCompletedFinanceRequests(
  companyId: number,
  page = 0,
  pageSize = 10,
  filters?: any,
  sortBy?: string,
  sortOrder: string = SortingOrder.DESC
): Promise<PaginatedResponse<APIBatchData>> {
  const params = new URLSearchParams();
  params.append('page', `${page}`);
  params.append('page_size', `${pageSize}`);
  params.append('sort_order', sortOrder);
  if (sortBy) {
    params.append('sort_by', `${decamelize(sortBy)}`);
  }
  filters && Object.keys(filters).forEach((key) => params.append(key, filters[key]));
  return axios
    .get(
      `/companies/${companyId}/products/factoring/services/funding_request_flow/completed/?${params.toString()}`
    )
    .then(({ data }) => data);
}

export function getFinanceRequests(
  companyId: number,
  filters?: any,
  isAdmin?: boolean
): Promise<PaginatedResponse<APIGetFinanceRequestDetail>> {
  const params = new URLSearchParams();
  filters &&
    Object.keys(filters).forEach((key) => {
      params.append(key, filters[key]);
    });
  return axios
    .get(
      `${
        isAdmin ? '/admins' : ''
      }/companies/${companyId}/products/factoring/services/funding_request_flow/?${params.toString()}`
    )
    .then(({ data }) => data);
}

export function getFinanceRequestAggregations(companyId: number): Promise<APIFinanceRequestAggregations> {
  return axios
    .get(`/companies/${companyId}/products/factoring/services/funding_request_flow/aggregations/`)
    .then(({ data }) => data);
}

export function initiateFinanceRequest(companyId: number): Promise<APIGetFinanceRequestDetail> {
  return axios
    .post(`/companies/${companyId}/products/factoring/services/funding_request_flow/`)
    .then(({ data }) => data);
}
export function initiateBulkFinanceRequest(
  companyId: number,
  payload: FormData
): Promise<APIGetFinanceRequestDetail> {
  return axios
    .post(`/companies/${companyId}/products/factoring/services/funding_request_flow/`, payload, {
      headers: {
        'Content-Type': `multipart/form-data; boundary=BoUnDaRyStRiNg`,
      },
    })
    .then(({ data }) => data);
}

export function getFinanceRequest(
  companyId: number,
  financeRequestId: string
): Promise<APIGetFinanceRequestDetail> {
  return axios
    .get(`/companies/${companyId}/products/factoring/services/funding_request_flow/${financeRequestId}/`)
    .then(({ data }) => data);
}

export function getFinanceRequestStatistics(
  companyId: number,
  financeRequestId: string
): Promise<APIGetFinanceRequestStatistics> {
  return axios
    .get(
      `/companies/${companyId}/products/factoring/services/funding_request_flow/${financeRequestId}/statistics/`
    )
    .then(({ data }) => data);
}

export function getFinanceRequestInvoices(
  companyId: number,
  financeRequestId: string,
  filters?: any
): Promise<APIGetFinanceRequestInvoices> {
  const params = new URLSearchParams();
  filters &&
    Object.keys(filters).forEach((key) => {
      params.append(key, filters[key]);
    });
  return axios
    .get(
      `/companies/${companyId}/products/factoring/services/funding_request_flow/${financeRequestId}/invoices/?${params.toString()}`
    )
    .then(({ data }) => data);
}

export function getInvoiceFields(
  file: FormData,
  companyId: number,
  financeRequestId: string
): Promise<APIGetInvoiceFields> {
  return axios
    .post(
      `/companies/${companyId}/products/factoring/services/funding_request_flow/${financeRequestId}/get_invoice_fields/`,
      file,
      {
        headers: {
          'Content-Type': `multipart/form-data; boundary=BoUnDaRyStRiNg`,
        },
      }
    )
    .then(({ data }) => data);
}

export function createInvoice(
  payload: FormData,
  companyId: number,
  financeRequestId: string
): Promise<APIFinanceRequestInvoice> {
  return axios
    .post(
      `/companies/${companyId}/products/factoring/services/funding_request_flow/${financeRequestId}/invoices/`,
      payload,
      {
        headers: {
          'Content-Type': `multipart/form-data; boundary=BoUnDaRyStRiNg`,
        },
      }
    )
    .then(({ data }) => data);
}
export function updateInvoice(
  payload: any,
  companyId: number,
  financeRequestId: string,
  invoiceId: number
): Promise<APIFinanceRequestInvoice> {
  return axios
    .patch(
      `/companies/${companyId}/products/factoring/services/funding_request_flow/${financeRequestId}/invoices/${invoiceId}/`,
      payload
    )
    .then(({ data }) => data);
}

export function updateInvoiceSupportingDocs(
  payload: { supportingDocumentsAreOptional: boolean },
  companyId: number,
  financeRequestId: string,
  invoiceId: number
): Promise<APIFinanceRequestInvoice> {
  return axios
    .patch(
      `/companies/${companyId}/products/factoring/services/funding_request_flow/${financeRequestId}/invoices/${invoiceId}/supporting_documents/`,
      payload
    )
    .then(({ data }) => data);
}

export function uploadInvoiceSupportingDocs(
  payload: FormData,
  companyId: number,
  financeRequestId: string,
  invoiceId: number
): Promise<APIFinanceRequestInvoice> {
  return axios
    .post(
      `/companies/${companyId}/products/factoring/services/funding_request_flow/${financeRequestId}/invoices/${invoiceId}/supporting_documents/`,
      payload,
      {
        headers: {
          'Content-Type': `multipart/form-data; boundary=BoUnDaRyStRiNg`,
        },
      }
    )
    .then(({ data }) => data);
}

export function deleteInvoiceSupportingDocs(
  companyId: number,
  financeRequestId: string,
  invoiceId: number,
  supportingDocId: number
): Promise<APIFinanceRequestInvoice> {
  return axios
    .delete(
      `/companies/${companyId}/products/factoring/services/funding_request_flow/${financeRequestId}/invoices/${invoiceId}/supporting_documents/${supportingDocId}/`
    )
    .then(({ data }) => data);
}

export function deleteInvoice(
  companyId: number,
  financeRequestId: string,
  invoiceId: number
): Promise<APIFinanceRequestInvoice> {
  return axios
    .delete(
      `/companies/${companyId}/products/factoring/services/funding_request_flow/${financeRequestId}/invoices/${invoiceId}/`
    )
    .then(({ data }) => data);
}

export function updateFinanceRequest(
  companyId: number,
  financeRequestId: string,
  payload: any
): Promise<APIGetFinanceRequestDetail> {
  return axios
    .patch(
      `/companies/${companyId}/products/factoring/services/funding_request_flow/${financeRequestId}/`,
      payload
    )
    .then(({ data }) => data);
}

export function reviewFinanceRequest(
  companyId: number,
  financeRequestId: string
): Promise<APIFinanceRequestReviewStatistics> {
  return axios
    .post(
      `/companies/${companyId}/products/factoring/services/funding_request_flow/${financeRequestId}/review_analytics/`
    )
    .then(({ data }) => data);
}

export function submitFinanceRequest(
  companyId: number,
  financeRequestId: string,
  payload: any
): Promise<APIGetFinanceRequestDetail> {
  return axios
    .post(
      `/companies/${companyId}/products/factoring/services/funding_request_flow/${financeRequestId}/submit/`,
      payload
    )
    .then(({ data }) => data);
}

export function signFinanceRequest(
  companyId: number,
  financeRequestId: string,
  payload: any
): Promise<APIGetFinanceRequestDetail> {
  return axios
    .post(
      `/companies/${companyId}/products/factoring/services/funding_request_flow/${financeRequestId}/sign/`,
      payload
    )
    .then(({ data }) => data);
}

export function getCompletedFinanceRequest(
  companyId: number,
  financeRequestId: string
): Promise<APIGetCompletedFinanceRequestDetail> {
  return axios
    .get(
      `/companies/${companyId}/products/factoring/services/funding_request_flow/completed/${financeRequestId}/`
    )
    .then(({ data }) => data);
}

export function getCompletedFinanceRequestInvoices(
  companyId: number,
  batchNumber: string,
  limit = 10,
  skip = 0
): Promise<APIGetCompletedFinanceRequestInvoices> {
  const params = new URLSearchParams();
  params.append('skip', `${skip}`);
  params.append('limit', `${limit}`);
  return axios
    .get(
      `/companies/${companyId}/products/factoring/services/funding_request_flow/completed/${batchNumber}/invoices/?${params.toString()}`
    )
    .then(({ data }) => data);
}

export function deleteFinanceRequest(companyId: number, financeRequestId: number) {
  return axios
    .delete(`/companies/${companyId}/products/factoring/services/funding_request_flow/${financeRequestId}/`)
    .then(({ data }) => data);
}

export const fetchAgingData = async (
  params: AgingDataParams,
  companyId: number
): Promise<AgingDataResponse[]> => {
  const { date_type, cutoff_date } = params;
  try {
    const response = await axios.get<AgingDataResponse[]>(
      `/companies/${companyId}/products/factoring/factoring_data/aging_history/?date_type=${date_type}&cutoff_date=${cutoff_date}`
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching aging data:', error);
    // Return empty array instead of throwing error to prevent app from crashing
    // This will trigger the no-data UI state instead of the error boundary
    return [];
  }
};

export const exportAgingData = async (params: AgingDataParams, companyId: number): Promise<Blob> => {
  const { date_type, cutoff_date } = params;
  try {
    const response = await axios.get(
      `/companies/${companyId}/products/factoring/factoring_data/aging_history/export/?date_type=${date_type}&cutoff_date=${cutoff_date}`,
      {
        responseType: 'blob',
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error exporting aging data:', error);
    // Create an empty blob instead of throwing error to prevent app from crashing
    const emptyBlob = new Blob([], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    return emptyBlob;
  }
};

export function getRevolvingCreditLineList(
  companyId: number
): Promise<PaginatedResponse<RevolvingCreditLineClient>> {
  return axios.get(`/companies/${companyId}/products/rcl/credit-lines/`).then(({ data }) => data);
}

export function getRevolvingCreditLine(
  companyId: number,
  creditLineId: string
): Promise<RevolvingCreditLineClientDetail> {
  return axios
    .get(`/companies/${companyId}/products/rcl/credit-lines/${creditLineId}/`)
    .then(({ data }) => data);
}

export const exportRevolvingCreditLineMovements = async (
  companyId: number,
  creditLineId: string
): Promise<Blob> => {
  const response = await axios.get(
    `/companies/${companyId}/products/rcl/credit-lines/${creditLineId}/movements/export/`,
    {
      responseType: 'blob',
    }
  );
  return response.data;
};

export function getRclRequests(companyId: number) {
  return axios.get(`/companies/${companyId}/products/rcl/product-requests/`).then(({ data }) => data);
}

export function submitRclRequest(companyId: number, requestedAmount: number) {
  return axios
    .post(`/companies/${companyId}/products/rcl/product-requests/`, { requestedAmount })
    .then(({ data }) => data);
}

export function getRevolvingCreditLinePendingCharges(
  companyId: number,
  creditLineId: string
): Promise<RevolvingCreditLinePendingCharges> {
  return axios
    .get(`/companies/${companyId}/products/rcl/credit-lines/${creditLineId}/pending-charges/`)
    .then(({ data }) => ({
      fullAmount: parseFloat(data.fullAmount),
      interestAmount: parseFloat(data.interestAmount),
      capitalizationAmount: parseFloat(data.capitalizationAmount),
    }));
}

export function submitPaymentRequest(
  companyId: number,
  creditLineId: string,
  payload: { amount: number; currency: string; description?: string }
): Promise<any> {
  return axios
    .post(`/companies/${companyId}/products/rcl/credit-lines/${creditLineId}/payments/`, payload)
    .then(({ data }) => data);
}

export function submitDrawRequest(
  companyId: number,
  creditLineId: string,
  payload: { amount: number; currency: string; notes?: string }
): Promise<any> {
  return axios
    .post(`/companies/${companyId}/products/rcl/credit-lines/${creditLineId}/draws/`, payload)
    .then(({ data }) => data);
}
